import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, RefreshCcw, TrendingUp, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useStaffAuth } from '../../context/StaffAuthContext';
import { apiErrorMessage } from '../../lib/apiError';
import { Button } from '../../components/ui/button';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function fmtCcy(map) {
  const entries = Object.entries(map || {});
  if (entries.length === 0) return <span className="text-[#0E1F1A]/45">—</span>;
  return entries.map(([ccy, v]) => (
    <span key={ccy} className="font-serif text-[15px] text-[#0B3B2E] block leading-tight">
      {ccy} {Number(v).toLocaleString('en-GB', { maximumFractionDigits: 0 })}
    </span>
  ));
}

export default function CrmReports() {
  const { staff, authHeader, API } = useStaffAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/crm/reports/won`, {
        headers: authHeader(), params: { year, month },
      });
      setData(res.data);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not load report'));
    } finally {
      setLoading(false);
    }
  }, [API, authHeader, year, month]);

  useEffect(() => { load(); }, [load]);

  if (staff.role === 'rm') {
    return (
      <div className="p-10 max-w-[900px] mx-auto">
        <div className="bg-white rounded-2xl border border-[#0B3B2E]/10 p-10 text-center">
          <TrendingUp className="w-8 h-8 mx-auto text-[#0B3B2E]/40" />
          <h1 className="mt-4 font-serif text-[24px] text-[#0B3B2E]">Admin & Manager only</h1>
          <p className="mt-2 text-[13px] text-[#0E1F1A]/60">Reports are visible to admins and sales managers.</p>
        </div>
      </div>
    );
  }

  const years = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#0B3B2E]/70 font-medium">CRM</p>
          <h1 className="mt-2 font-serif text-[32px] lg:text-[40px] leading-tight text-[#0B3B2E]">
            Won deals report
          </h1>
          <p className="mt-1 text-[13px] text-[#0E1F1A]/60">
            Deals marked <b>won</b> in the selected month, grouped by RM and by referral partner.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))}
            className="h-10 rounded-md bg-white border border-[#0B3B2E]/15 px-3 text-[14px]">
            {MONTHS.map((m, i) => <option key={m} value={i+1}>{m}</option>)}
          </select>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}
            className="h-10 rounded-md bg-white border border-[#0B3B2E]/15 px-3 text-[14px]">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <Button onClick={load} variant="outline"
            className="h-10 rounded-full border-[#0B3B2E]/25 bg-transparent text-[#0B3B2E] hover:bg-[#0B3B2E]/5">
            <RefreshCcw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="mt-8 p-16 flex items-center justify-center text-[#0B3B2E] bg-white rounded-2xl border border-[#0B3B2E]/10">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : !data ? null : (
        <>
          {/* Grand total */}
          <div className="mt-6 bg-[#0B3B2E] text-[#F5F1E8] rounded-2xl p-6 lg:p-8">
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#C8944B]">
              Grand total won · {MONTHS[data.month - 1]} {data.year}
            </p>
            <div className="mt-3 flex flex-wrap gap-x-8 gap-y-2">
              {Object.keys(data.grand_total_by_currency || {}).length === 0 ? (
                <p className="font-serif text-[36px] leading-none opacity-70">No won deals this month</p>
              ) : (
                Object.entries(data.grand_total_by_currency).map(([c, v]) => (
                  <p key={c} className="font-serif text-[36px] lg:text-[46px] leading-none">
                    <span className="text-[#C8944B] text-[20px] lg:text-[24px] mr-2">{c}</span>
                    {Number(v).toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                  </p>
                ))
              )}
            </div>
          </div>

          {/* Two-column tables */}
          <div className="mt-6 grid lg:grid-cols-2 gap-4">
            {/* By RM */}
            <div className="bg-white rounded-2xl border border-[#0B3B2E]/10 overflow-hidden">
              <div className="px-5 py-4 border-b border-[#0B3B2E]/10 flex items-center gap-2">
                <Users className="w-4 h-4 text-[#0B3B2E]" />
                <h2 className="font-serif text-[20px] text-[#0B3B2E]">By relationship manager</h2>
              </div>
              {data.by_rm.length === 0 ? (
                <p className="p-8 text-center text-[13px] text-[#0E1F1A]/55">No wins in this month.</p>
              ) : (
                <table className="w-full text-[13px]">
                  <thead className="bg-[#0B3B2E]/5 text-[#0B3B2E]">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-medium">RM</th>
                      <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-medium">Deals</th>
                      <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-medium">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_rm.map((r) => (
                      <tr key={r.rm_id || 'unassigned'} className="border-t border-[#0B3B2E]/8">
                        <td className="px-4 py-3 font-medium text-[#0B3B2E]">
                          {r.rm_name}
                          {r.role && <span className="ml-2 text-[10px] text-[#0E1F1A]/50 uppercase tracking-wider">{r.role}</span>}
                        </td>
                        <td className="px-4 py-3">{r.count}</td>
                        <td className="px-4 py-3">{fmtCcy(r.by_currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* By partner */}
            <div className="bg-white rounded-2xl border border-[#0B3B2E]/10 overflow-hidden">
              <div className="px-5 py-4 border-b border-[#0B3B2E]/10 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#0B3B2E]" />
                <h2 className="font-serif text-[20px] text-[#0B3B2E]">By referral partner</h2>
              </div>
              {data.by_partner.length === 0 ? (
                <p className="p-8 text-center text-[13px] text-[#0E1F1A]/55">No partner-referred wins this month.</p>
              ) : (
                <table className="w-full text-[13px]">
                  <thead className="bg-[#0B3B2E]/5 text-[#0B3B2E]">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-medium">Partner</th>
                      <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-medium">Deals</th>
                      <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-medium">Value</th>
                      <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-medium">Commission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_partner.map((p) => (
                      <tr key={p.partner_id || 'direct'} className="border-t border-[#0B3B2E]/8">
                        <td className="px-4 py-3">
                          <p className="font-medium text-[#0B3B2E]">{p.partner_company}</p>
                          {p.commission_pct > 0 && (
                            <p className="text-[10px] text-[#0E1F1A]/50 uppercase tracking-wider">{p.commission_pct}% commission</p>
                          )}
                        </td>
                        <td className="px-4 py-3">{p.count}</td>
                        <td className="px-4 py-3">{fmtCcy(p.by_currency)}</td>
                        <td className="px-4 py-3">{fmtCcy(p.commission_by_currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
